// components/alerts/NotificationDetailModal.jsx
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    useTheme
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const NotificationDetailModal = ({ notification, open, onClose }) => {
    const theme = useTheme();

    if (!notification) return null;

    // Notification type info mapping
    const notificationTypeInfo = {
        priceAlert: {
            title: 'Price Alert',
            icon: <TrendingUpIcon fontSize="medium" />,
            color: '#4CAF50'
        },
        newsAlert: {
            title: 'News Alert',
            icon: <ArticleIcon fontSize="medium" />,
            color: '#2196F3'
        },
        congressTrade: {
            title: 'Congressional Trading Alert',
            icon: <AccountBalanceIcon fontSize="medium" />,
            color: '#9C27B0'
        },
        insiderTrade: {
            title: 'Insider Trading Alert',
            icon: <BusinessIcon fontSize="medium" />,
            color: '#FF9800'
        },
        marketEvent: {
            title: 'Market Event Alert',
            icon: <EventIcon fontSize="medium" />,
            color: '#F44336'
        }
    };

    const typeInfo = notificationTypeInfo[notification.type] || {
        title: 'Notification',
        icon: <NotificationsIcon fontSize="medium" />,
        color: '#757575'
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Get metadata display based on notification type
    const getMetadataDisplay = () => {
        const { metadata, type } = notification;
        if (!metadata) return null;

        switch (type) {
            case 'priceAlert':
                return (
                    <Box sx={{ mt: 2 }}>
                        {metadata.symbol && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Symbol:</strong> {metadata.symbol}
                            </Typography>
                        )}
                        {metadata.price !== undefined && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Current Price:</strong> ${typeof metadata.price === 'number' ? metadata.price.toFixed(2) : metadata.price}
                            </Typography>
                        )}
                        {metadata.changePercent !== undefined && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Change:</strong>{' '}
                                <span style={{ color: metadata.changePercent >= 0 ? '#4CAF50' : '#f44336' }}>
                                    {metadata.changePercent >= 0 ? '+' : ''}{metadata.changePercent.toFixed(2)}%
                                </span>
                            </Typography>
                        )}
                    </Box>
                );

            case 'newsAlert':
                return (
                    <Box sx={{ mt: 2 }}>
                        {metadata.source && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Source:</strong> {metadata.source}
                            </Typography>
                        )}
                        {metadata.url && (
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    startIcon={<OpenInNewIcon />}
                                    href={metadata.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Read Full Article
                                </Button>
                            </Box>
                        )}
                    </Box>
                );

            case 'congressTrade':
                return (
                    <Box sx={{ mt: 2 }}>
                        {metadata.representative && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Representative:</strong> {metadata.representative}
                            </Typography>
                        )}
                        {metadata.symbol && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Symbol:</strong> {metadata.symbol}
                            </Typography>
                        )}
                        {metadata.tradeType && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Trade Type:</strong> {metadata.tradeType}
                            </Typography>
                        )}
                        {metadata.amount && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Amount:</strong> {metadata.amount}
                            </Typography>
                        )}
                    </Box>
                );

            case 'insiderTrade':
                return (
                    <Box sx={{ mt: 2 }}>
                        {metadata.name && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Insider:</strong> {metadata.name}
                            </Typography>
                        )}
                        {metadata.symbol && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Symbol:</strong> {metadata.symbol}
                            </Typography>
                        )}
                        {metadata.transactionType && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Transaction Type:</strong> {metadata.transactionType}
                            </Typography>
                        )}
                        {metadata.value && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Value:</strong> ${typeof metadata.value === 'number' ? Math.abs(metadata.value).toLocaleString() : metadata.value}
                            </Typography>
                        )}
                    </Box>
                );

            case 'marketEvent':
                return (
                    <Box sx={{ mt: 2 }}>
                        {metadata.type && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Event Type:</strong> {metadata.type}
                            </Typography>
                        )}
                        {metadata.symbol && (
                            <Typography variant="body2" gutterBottom>
                                <strong>Symbol:</strong> {metadata.symbol}
                            </Typography>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark'
                        ? '#0A1929' // Same dark background as your app
                        : '#ffffff',
                    backgroundImage: 'none',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            <DialogTitle sx={{
                m: 0,
                p: 2,
                display: 'flex',
                alignItems: 'center',
            }}>
                <Box
                    sx={{
                        mr: 2,
                        color: typeInfo.color,
                        display: 'flex'
                    }}
                >
                    {typeInfo.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{notification.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {typeInfo.title} • {formatDate(notification.createdAt)}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                            color: 'white',
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {notification.content}
                </Typography>

                {getMetadataDisplay()}

                {notification.deliveries && notification.deliveries.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Delivered via:{' '}
                            {notification.deliveries
                                .filter(d => d.status === 'delivered' || d.status === 'sent')
                                .map(d => d.channel)
                                .join(', ')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    variant="text"
                    sx={{
                        color: 'white',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationDetailModal;

